<template>
    <div class="search-card">
        <slot>
            <template v-if="item && uri">
                <nuxt-link
                    v-if="title"
                    :to="`/${uri}`"
                    class="search-card__link"
                >
                    <h4 class="search-card__title">{{ title }}</h4>
                </nuxt-link>
                <p
                    v-if="item.searchDescription"
                    class="search-card__description"
                >
                    {{ item.searchDescription }}
                </p>
                <!-- eslint-disable vue/no-v-html -->
                <div
                    v-else-if="fallback"
                    class="search-card__fallback"
                    v-html="fallback"
                />
                <!-- eslint-enable vue/no-v-html -->
                <p
                    v-else-if="fallbackString"
                    class="search-card__description"
                >
                    {{ fallbackString }}
                </p>
                <BasePill
                    v-if="type"
                    class="search-card__type"
                >
                    {{ type }}
                </BasePill>
            </template>
        </slot>
    </div>
</template>

<script setup>
const t = useTranslation('types');

const entryTypeMapping = {
    'contact_contact_Entry': 'Contact page',

    'offices_office_Entry': 'Office',
    'offices_officePage_Entry': 'Office info',
    'offices_officeMember_Entry': 'Office member',

    'eauSections_eauSection_Entry': 'Section',
    'eauSections_eauSectionPage_Entry': 'Section info',
    'eauSections_eauSectionMember_Entry': 'Section member',

    'guidelinesOverview_guidelinesOverview_Entry': 'Guideline overview',
    'guidelinesArchive_guidelinesArchive_Entry': 'Guideline archive',
    'guidelines_guideline_Entry': 'Guideline',
    'guidelines_guidelineChapter_Entry': 'Guideline chapter',

    'educationAndEvents_live_Entry': 'Event',
    'educationAndEvents_onDemand_Entry': 'On-demand education',

    'news_news_Entry': 'News',
    'page_page_Entry': 'Page',
    'home_home_Entry': 'Home',
    'topics_topic_Entry': 'Topic',
    'books_books_Entry': 'Programme',
    'pressReleases_pressRelease_Entry': 'Press release',

    'condition_condition_Entry': t('condition'),
    'condition_stage_Entry': t('stage'),
    'condition_treatment_Entry': t('treatment'),
};

const props = defineProps({
    item: {
        type: Object,
        default() {
            return {};
        }
    }
});

const type = computed(() => entryTypeMapping[props.item?.entryType]);

const isParentUriType = (entryType) => [
    'eauSections_eauSectionMember_Entry',
    'offices_officeMember_Entry'
].includes(entryType);

const isMemberType = (entryType) => [
    'eauSections_eauSectionMember_Entry',
    'offices_officeMember_Entry'
].includes(entryType);

const stripTags = (str) => str ? str.replace(/<[^>]*>?/gm, ' ') : '';

const uri = computed(() => isParentUriType(props.item?.entryType) ?
    props.item.parent?.uri :
    props.item.uri
);

const hasGuidelineParent = computed(() =>
    !!(props.item && props.item.entryType === 'guidelines_guidelineChapter_Entry' && props.item.parent)
);
const isAMember = computed(() =>
    !!(props.item && (isMemberType(props.item.entryType)))
);

const title = computed(() => hasGuidelineParent.value ?
    `${props.item.parent.title} : ${props.item.title}` :
    props.item.title
);
const fallback = computed(() => !isAMember.value ? stripTags(props.item.fallback) : '');

const fallbackString = computed(() => {
    if (isAMember.value) {
        if (props.item.parent) {
            return `${props.item.parent.title} : ${props.item.fallback}`;
        }
        return stripTags(props.item.fallback);
    }

    // this is for guidelines_guideline_Entry
    if (props.item && props.item.fallbackObject && props.item.fallbackObject.length) {
        return props.item.fallbackObject[0].title;
    }

    if (hasGuidelineParent.value) {
        if (props.item.parent.searchDescription) {
            return props.item.parent.searchDescription;
        } else {
            return props.item.parent.fallbackObject[0].title;
        }
    }
    return '';
});
</script>

<style lang="less">
.search-card {
    margin-top: 2.5rem;
    padding-bottom: var(--spacing-md);
    border-bottom: 1px solid var(--color-light-gray);

    &:first-child {
        margin-top: 0;
    }
}


.search-card__link {
    .link-clean();

}

.search-card__title {
    margin-bottom: var(--spacing-md) !important; /* stylelint-disable-line declaration-no-important */

    .search-card__link:hover > & {
        color: var(--color-primary);
    }

    .search-card__link > &:active,
    .search-card__link > &:focus {
        color: var(--color-primary-darkerst);
    }
}

.search-card__description,
.search-card__fallback {
    .typography-text();
    margin: 0 auto var(--spacing-md) 0;
}

.search-card__fallback {
    max-height: 4.5em;
    overflow: hidden;

    @media @q-md-min {
        max-height: 3em;
    }
}

.search-card__type {
    .typography-text();
    color: var(--color-eau-blue);
}
</style>
